import React, { useCallback, useMemo, useReducer } from "react"
import type { FetcherWithComponents } from "react-router"
import { LoaderCircleIcon, SaveIcon, XCircleIcon, TrashIcon, PencilIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Badge } from "@/components/ui/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { PopoverClose } from "@radix-ui/react-popover"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
  DialogDescription,
} from "@/components/ui/dialog"

import type { SubClassificationsType } from "@/utils/types"

export interface EvidenceThemesFetcherData {
  success?: boolean
  action?: string
  errorMessage?: string
}

export interface EvidenceThemesFormValues {
  enableFilters: boolean
  enableForUnknownClassifications: boolean
  highlightColors: Record<string, string>
  hiddenThemes: Record<string, boolean>
}

export interface EvidenceThemesConfigLike {
  enableFilters?: boolean
  enableForUnknownClassifications?: boolean
  hiddenThemes?: Record<string, boolean>
  highlightColors?: Record<string, string>
}

export interface DocumentClassificationGroup {
  name: string
  subClassifications: SubClassificationsType[]
}

export interface EvidenceThemeDataItem {
  name: string
  usage: string
}

export interface ClassificationMappingItem {
  classification: string
  lens: { code: string; name: string }
  themes: Array<{ name: string; usage: string }>
}

export interface EvidenceThemesSharedData {
  evidenceThemeData?: EvidenceThemeDataItem[]
  classificationMappings?: ClassificationMappingItem[]
  documentClassifications?: DocumentClassificationGroup[]
}

export type EvidenceThemesPendingAction = "SAVE_SECTION" | "REMOVE_SECTION" | null

interface SharedEvidenceThemesTabProps {
  config: EvidenceThemesConfigLike | undefined
  data: EvidenceThemesSharedData
  fetcher: FetcherWithComponents<EvidenceThemesFetcherData>
  pendingAction: EvidenceThemesPendingAction
  onSave: (values: EvidenceThemesFormValues) => void
  onRemove: () => void
}

type HiddenThemesAction = { type: "ADD"; themes: string[] } | { type: "REMOVE"; theme: string } | { type: "RESET" }

function hiddenThemesReducer(state: string[], action: HiddenThemesAction): string[] {
  switch (action.type) {
    case "ADD": {
      const existing = new Set(state)
      const incoming = action.themes.filter((t) => !existing.has(t))
      return incoming.length === 0 ? state : [...state, ...incoming]
    }
    case "REMOVE":
      return state.filter((t) => t !== action.theme)
    case "RESET":
      return []
    default:
      return state
  }
}

type HighlightColor = { theme: string; color: string }
type HighlightColorsAction =
  | { type: "ADD"; theme: string; color: string }
  | { type: "EDIT"; theme: string; color: string }
  | { type: "REMOVE"; theme: string }
  | { type: "RESET" }

function highlightColorsReducer(state: HighlightColor[], action: HighlightColorsAction): HighlightColor[] {
  switch (action.type) {
    case "ADD":
      return [{ theme: action.theme, color: action.color }, ...state]
    case "EDIT":
      return state.map((item) => (item.theme === action.theme ? { ...item, color: action.color } : item))
    case "REMOVE":
      return state.filter((item) => item.theme !== action.theme)
    case "RESET":
      return []
    default:
      return state
  }
}

export function SharedEvidenceThemesTab({
  config,
  data,
  fetcher,
  pendingAction,
  onSave,
  onRemove,
}: SharedEvidenceThemesTabProps) {
  const documentClassifications = data.documentClassifications

  const allThemes = useMemo(
    () =>
      (data.evidenceThemeData ?? [])
        .filter((theme) => theme.usage === "bookmark")
        .map((theme) => theme.name)
        .sort(),
    [data.evidenceThemeData],
  )

  const flattenedMapping = useMemo(
    () =>
      (data.classificationMappings ?? []).flatMap(({ classification, lens, themes }) =>
        themes.map((theme) => ({
          classification,
          theme,
          lensCode: lens.code,
          lensName: lens.name,
          id: `${classification}-${lens.code}-${theme.name}-${theme.usage}`,
        })),
      ),
    [data.classificationMappings],
  )

  const [selectedGroup, setSelectedGroup] = React.useState<string>("ALL")
  const [selectedClassification, setSelectedClassification] = React.useState<string>("ALL")
  const [selectedTheme, setSelectedTheme] = React.useState<string>("ALL")

  const [enableFilters, setEnableFilters] = React.useState(config?.enableFilters ?? false)
  const [enableForUnknownClassifications, setEnableForUnknownClassifications] = React.useState(
    config?.enableForUnknownClassifications ?? false,
  )

  const [hiddenThemes, dispatchHiddenThemes] = useReducer(hiddenThemesReducer, undefined, (): string[] => {
    const raw = config?.hiddenThemes
    if (raw == null) return []
    return Object.entries(raw).reduce<string[]>((acc, [key, value]) => {
      if (value === true) acc.push(key)
      return acc
    }, [])
  })

  const [highlightColors, dispatchHighlightColors] = useReducer(
    highlightColorsReducer,
    undefined,
    (): HighlightColor[] => {
      const raw = config?.highlightColors
      if (raw == null) return []
      return Object.entries(raw).map(([theme, color]) => ({ theme, color: String(color) }))
    },
  )

  const handleColorChange = useCallback((theme: string, color: string) => {
    dispatchHighlightColors({ type: "EDIT", theme, color })
  }, [])

  const handleRemoveHighlightColor = useCallback((theme: string) => {
    dispatchHighlightColors({ type: "REMOVE", theme })
  }, [])

  const handleAddHighlightColor = useCallback((theme: string, color: string) => {
    dispatchHighlightColors({ type: "ADD", theme, color })
  }, [])

  const availableThemesForHighlightModal = useMemo(() => {
    const usedThemes = new Set(highlightColors.map((item) => item.theme))
    return allThemes.filter((t) => !usedThemes.has(t))
  }, [allThemes, highlightColors])

  const handleAddHiddenThemes = useCallback((themes: string[]) => {
    dispatchHiddenThemes({ type: "ADD", themes })
  }, [])

  const handleRemoveHiddenTheme = useCallback((theme: string) => {
    dispatchHiddenThemes({ type: "REMOVE", theme })
  }, [])

  const availableThemesForModal = useMemo(() => {
    const hiddenSet = new Set(hiddenThemes)
    return allThemes.filter((t) => !hiddenSet.has(t))
  }, [allThemes, hiddenThemes])

  const classificationToGroup = useMemo(() => {
    const map = new Map<string, string>()
    documentClassifications?.forEach((group) => {
      group.subClassifications.forEach((sub: SubClassificationsType) => {
        map.set(sub.name, group.name)
      })
    })
    return map
  }, [documentClassifications])

  const getGroupForClassification = useCallback(
    (classification: string) => classificationToGroup.get(classification) ?? "Unknown",
    [classificationToGroup],
  )

  const groupAndClassificationFilteredMapping = useMemo(() => {
    return flattenedMapping.filter((item) => {
      const group = getGroupForClassification(item.classification)
      if (selectedGroup !== "ALL" && group !== selectedGroup) return false
      if (selectedClassification !== "ALL" && item.classification !== selectedClassification) return false
      return true
    })
  }, [flattenedMapping, selectedGroup, selectedClassification, getGroupForClassification])

  const filteredMapping = useMemo(() => {
    if (selectedTheme === "ALL") return groupAndClassificationFilteredMapping
    return groupAndClassificationFilteredMapping.filter((item) => item.theme.name === selectedTheme)
  }, [groupAndClassificationFilteredMapping, selectedTheme])

  const uniqueGroups = useMemo(() => {
    if (documentClassifications == null) return []
    return documentClassifications.map((group) => group.name).sort()
  }, [documentClassifications])

  const uniqueClassifications = useMemo(() => {
    if (selectedGroup === "ALL") {
      const allClassifications = new Set<string>()
      flattenedMapping.forEach((item) => allClassifications.add(item.classification))
      return Array.from(allClassifications).sort()
    }
    const selectedGroupData = documentClassifications?.find((group) => group.name === selectedGroup)
    if (selectedGroupData == null) return []
    return selectedGroupData.subClassifications.map((sub: SubClassificationsType) => sub.name).sort()
  }, [selectedGroup, documentClassifications, flattenedMapping])

  const uniqueThemes = useMemo(() => {
    const themes = new Set<string>()
    groupAndClassificationFilteredMapping.forEach((item) => themes.add(item.theme.name))
    return Array.from(themes).sort()
  }, [groupAndClassificationFilteredMapping])

  const handleGroupChange = useCallback((value: string) => {
    setSelectedGroup(value)
    setSelectedClassification("ALL")
    setSelectedTheme("ALL")
  }, [])

  const handleClassificationChange = useCallback((value: string) => {
    setSelectedClassification(value)
    setSelectedTheme("ALL")
  }, [])

  const handleClearFilters = useCallback(() => {
    setSelectedGroup("ALL")
    setSelectedClassification("ALL")
    setSelectedTheme("ALL")
  }, [])

  const handleSubmit = useCallback(
    (formEvent: React.FormEvent<HTMLFormElement>) => {
      formEvent.preventDefault()
      onSave({
        enableFilters,
        enableForUnknownClassifications,
        highlightColors: Object.fromEntries(highlightColors.map(({ theme, color }) => [theme, color])),
        hiddenThemes: Object.fromEntries(hiddenThemes.map((theme) => [theme, true])),
      })
    },
    [onSave, enableFilters, enableForUnknownClassifications, highlightColors, hiddenThemes],
  )

  return (
    <div className="flex h-full flex-col">
      <fetcher.Form onSubmit={handleSubmit} className="flex h-full flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <div className="mx-auto grid w-full grid-cols-1 gap-6 xl:grid-cols-4">
            <div className="space-y-6 xl:col-span-1">
              <div className="rounded-lg border p-4">
                <h3 className="mb-4 text-lg font-medium">Settings</h3>
                <div className="space-y-4">
                  <div className="space-y-3">
                    <label className="flex items-start gap-3">
                      <Checkbox
                        checked={enableFilters}
                        onCheckedChange={(checked) => setEnableFilters(checked === true)}
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <span className="text-sm font-medium">Enable Filters</span>
                        <p className="text-xs text-muted-foreground">Allow users to filter evidence by themes</p>
                      </div>
                    </label>

                    <label className="flex items-start gap-3">
                      <Checkbox
                        checked={enableForUnknownClassifications}
                        onCheckedChange={(checked) => setEnableForUnknownClassifications(checked === true)}
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <span className="text-sm font-medium">Enable for Unknown Classifications</span>
                        <p className="text-xs text-muted-foreground">
                          Show themes for documents without classification
                        </p>
                      </div>
                    </label>
                  </div>

                  <div className="space-y-3 border-t pt-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium">Hidden Themes</h4>
                      <HiddenThemesDialog availableThemes={availableThemesForModal} onAdd={handleAddHiddenThemes} />
                    </div>

                    {hiddenThemes.length === 0 ? (
                      <div className="rounded-lg border-2 border-dashed border-border py-6 text-center text-muted-foreground">
                        <p className="text-xs">No hidden themes</p>
                      </div>
                    ) : (
                      <div className="max-h-48 space-y-2 overflow-y-auto pr-1">
                        {hiddenThemes.map((theme) => (
                          <div
                            key={theme}
                            className="flex items-center justify-between rounded border bg-primary-foreground px-3 py-2"
                          >
                            <span className="text-sm">{theme}</span>
                            <DeleteConfirmPopover
                              itemLabel={theme}
                              itemType="Hidden Theme"
                              ariaLabel={`Remove hidden theme ${theme}`}
                              onConfirm={() => handleRemoveHiddenTheme(theme)}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="rounded-lg border p-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-md font-medium">Highlight Colors</h4>
                    <HighlightColorAddDialog
                      availableThemes={availableThemesForHighlightModal}
                      onAdd={handleAddHighlightColor}
                    />
                  </div>

                  {highlightColors.length === 0 ? (
                    <div className="rounded-lg border-2 border-dashed border-border py-6 text-center text-muted-foreground">
                      <p className="text-xs">No highlight colors</p>
                    </div>
                  ) : (
                    <div className="max-h-64 space-y-3 overflow-y-auto pr-1">
                      {highlightColors.map(({ theme, color }) => (
                        <HighlightColorRow
                          key={theme}
                          theme={theme}
                          color={color}
                          onColorChange={handleColorChange}
                          onRemove={handleRemoveHighlightColor}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex min-h-0 flex-col rounded-lg border xl:col-span-3">
              <div className="flex-shrink-0 border-b p-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium">Classification to Theme Mappings</h3>
                  {(selectedGroup !== "ALL" || selectedClassification !== "ALL" || selectedTheme !== "ALL") && (
                    <Button type="button" variant="ghost" size="sm" onClick={handleClearFilters}>
                      Clear Filters
                    </Button>
                  )}
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto p-4">
                <div className="mb-4 rounded border-l-4 border-info bg-info/10 p-3">
                  <a
                    href="/applications/Unity%20Viewer/brand-document-classification-evidence-themes"
                    rel="noopener noreferrer"
                    className="text-sm text-info hover:underline"
                  >
                    Edit classification filters →
                  </a>
                </div>

                <div className="mb-4 grid grid-cols-3 gap-3">
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">Group</label>
                    <Select value={selectedGroup} onValueChange={handleGroupChange}>
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="All Groups" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">All Groups</SelectItem>
                        {uniqueGroups.map((group) => (
                          <SelectItem key={group} value={group}>
                            {group}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">Classification</label>
                    <Select value={selectedClassification} onValueChange={handleClassificationChange}>
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="All Classifications" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">All Classifications</SelectItem>
                        {uniqueClassifications.map((classification: string) => (
                          <SelectItem key={classification} value={classification}>
                            {classification}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">Theme</label>
                    <Select value={selectedTheme} onValueChange={setSelectedTheme}>
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="All Themes" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">All Themes</SelectItem>
                        {uniqueThemes.map((theme) => (
                          <SelectItem key={theme} value={theme}>
                            {theme}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {(selectedGroup !== "ALL" || selectedClassification !== "ALL" || selectedTheme !== "ALL") && (
                  <div className="mb-3 text-sm text-muted-foreground">
                    Showing {filteredMapping.length} of {flattenedMapping.length} mappings
                  </div>
                )}

                {filteredMapping.length === 0 ? (
                  <div className="rounded-lg border-2 border-dashed border-border py-16 text-center text-muted-foreground">
                    <p className="text-lg">
                      {flattenedMapping.length === 0
                        ? "No classification mappings found"
                        : "No mappings match the selected filters"}
                    </p>
                    {flattenedMapping.length > 0 && (
                      <Button type="button" variant="outline" className="mt-4" onClick={handleClearFilters}>
                        Clear Filters
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="overflow-auto pr-2">
                    <table className="w-full">
                      <thead className="border-b">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium">Group</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Classification</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Lens</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Theme</th>
                          <th className="px-4 py-3 text-left text-sm font-medium">Usage</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredMapping.map((item) => {
                          const group = getGroupForClassification(item.classification)
                          return (
                            <tr key={item.id} className="border-b hover:bg-primary-foreground">
                              <td className="px-4 py-3 text-sm">{group}</td>
                              <td className="px-4 py-3 text-sm">{item.classification}</td>
                              <td className="px-4 py-3 text-sm">
                                <Badge variant="outline">{item.lensName}</Badge>
                              </td>
                              <td className="px-4 py-3 text-sm">{item.theme.name}</td>
                              <td className="px-4 py-3">
                                <Badge variant="secondary">{item.theme.usage}</Badge>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 border-t p-4">
          <div className="mx-auto flex w-full items-center gap-3">
            <Button
              type="submit"
              disabled={fetcher.state !== "idle"}
              className="flex-1 bg-info px-6 py-2.5 hover:bg-info/90"
            >
              {pendingAction === "SAVE_SECTION" && fetcher.state !== "idle" && (
                <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />
              )}
              <SaveIcon className="h-4 w-4" />
              Save Evidence Themes
            </Button>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="default"
                  className="flex-1 gap-2 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                  disabled={fetcher.state !== "idle"}
                >
                  {pendingAction === "REMOVE_SECTION" && fetcher.state !== "idle" ? (
                    <LoaderCircleIcon className="h-4 w-4 animate-spin" />
                  ) : (
                    <XCircleIcon className="h-4 w-4" />
                  )}
                  <span className="hidden sm:inline">Remove Section</span>
                  <span className="sm:hidden">Remove</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent side="top" className="w-80">
                <div className="space-y-3">
                  <p className="text-sm font-medium">Remove Evidence Themes Configuration?</p>
                  <p className="text-sm text-muted-foreground">
                    This will remove all evidence theme configurations including filters and colors.
                  </p>
                  <div className="flex items-center justify-end gap-3">
                    <PopoverClose asChild>
                      <Button type="button" variant="ghost" size="sm">
                        Cancel
                      </Button>
                    </PopoverClose>
                    <PopoverClose asChild>
                      <Button type="button" variant="destructive" size="sm" onClick={onRemove}>
                        Remove
                      </Button>
                    </PopoverClose>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </fetcher.Form>
    </div>
  )
}

function HiddenThemesDialog({
  availableThemes,
  onAdd,
}: {
  availableThemes: string[]
  onAdd: (themes: string[]) => void
}) {
  const [selected, setSelected] = React.useState<string[]>([])

  return (
    <Dialog
      onOpenChange={(open) => {
        if (open) setSelected([])
      }}
    >
      <DialogTrigger asChild>
        <Button type="button" size="sm" variant="outline" disabled={availableThemes.length === 0}>
          + Add
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Add Hidden Themes</DialogTitle>
          <DialogDescription className="sr-only">
            Select the themes you want to hide from evidence display.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-64 space-y-3 overflow-y-auto pr-1">
          {availableThemes.map((theme) => (
            <label key={theme} className="flex items-center gap-2">
              <Checkbox
                checked={selected.includes(theme)}
                onCheckedChange={(checked) => {
                  setSelected((prev) => (checked === true ? [...prev, theme] : prev.filter((t) => t !== theme)))
                }}
              />
              <span className="text-sm">{theme}</span>
            </label>
          ))}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <DialogClose asChild>
            <Button type="button" variant="ghost" size="sm">
              Cancel
            </Button>
          </DialogClose>
          <DialogClose asChild>
            <Button type="button" size="sm" disabled={selected.length === 0} onClick={() => onAdd(selected)}>
              Add
            </Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function HighlightColorAddDialog({
  availableThemes,
  onAdd,
}: {
  availableThemes: string[]
  onAdd: (theme: string, color: string) => void
}) {
  const [open, setOpen] = React.useState(false)
  const [theme, setTheme] = React.useState("")
  const [color, setColor] = React.useState("#3b82f6")
  const [addAnother, setAddAnother] = React.useState(false)

  const handleAdd = useCallback(() => {
    if (!theme) return
    onAdd(theme, color)
    setTheme("")
    setColor("#3b82f6")
    if (!addAnother) setOpen(false)
  }, [theme, color, addAnother, onAdd])

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (next) {
          setTheme("")
          setColor("#3b82f6")
          setAddAnother(false)
        }
      }}
    >
      <DialogTrigger asChild>
        <Button type="button" size="sm" variant="outline" disabled={availableThemes.length === 0}>
          + Add
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Add Highlight Colour</DialogTitle>
          <DialogDescription className="sr-only">Choose a theme and assign a highlight colour to it.</DialogDescription>
        </DialogHeader>
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">Select Theme</label>
          <select
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
            className="w-full rounded border border-input bg-background p-2 text-sm text-foreground"
          >
            <option value="">-- Select a Theme --</option>
            {availableThemes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div className="mt-3">
          <label className="mb-1 block text-xs text-muted-foreground">Choose Colour</label>
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="h-10 w-full cursor-pointer rounded border"
          />
        </div>
        <label className="mt-4 flex items-center gap-2">
          <Checkbox checked={addAnother} onCheckedChange={(checked) => setAddAnother(checked === true)} />
          <span className="text-sm">Add another?</span>
        </label>
        <div className="mt-4 flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button type="button" size="sm" disabled={!theme} onClick={handleAdd}>
            Add
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function HighlightColorRow({
  theme,
  color,
  onColorChange,
  onRemove,
}: {
  theme: string
  color: string
  onColorChange: (theme: string, color: string) => void
  onRemove: (theme: string) => void
}) {
  const inputId = React.useId()

  const [draftColor, setDraftColor] = React.useState<string | undefined>(undefined)

  return (
    <div className="rounded-lg border bg-primary-foreground p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <input
            id={inputId}
            type="color"
            value={draftColor ?? color}
            onChange={(e) => setDraftColor(e.target.value)}
            aria-label={`Current highlight colour for ${theme}`}
            className="h-5 w-5 flex-shrink-0 cursor-pointer rounded border p-0"
          />
          <span className="text-sm font-medium">{theme}</span>
        </div>
        <div className="flex items-center gap-2">
          <label
            htmlFor={inputId}
            aria-label={`Edit highlight colour for ${theme}`}
            className="cursor-pointer text-blue-400 hover:text-blue-600"
          >
            <PencilIcon className="h-4 w-4" />
          </label>
          <DeleteConfirmPopover
            itemLabel={theme}
            itemType="Highlight Colour"
            ariaLabel={`Remove highlight colour for ${theme}`}
            onConfirm={() => onRemove(theme)}
          />
        </div>
      </div>

      {draftColor !== undefined && (
        <div className="mt-2 flex justify-end gap-2">
          <Button type="button" size="sm" variant="ghost" onClick={() => setDraftColor(undefined)}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => {
              onColorChange(theme, draftColor)
              setDraftColor(undefined)
            }}
          >
            Save
          </Button>
        </div>
      )}
    </div>
  )
}

function DeleteConfirmPopover({
  itemLabel,
  itemType,
  onConfirm,
  ariaLabel,
}: {
  itemLabel: string
  itemType: string
  onConfirm: () => void
  ariaLabel: string
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" aria-label={ariaLabel} className="text-red-400 hover:text-red-600">
          <TrashIcon className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" className="w-72">
        <div className="space-y-3">
          <p className="text-sm text-foreground">
            <span className="font-medium">&ldquo;{itemLabel}&rdquo;</span> will be removed as a {itemType}. Are you
            sure?
          </p>
          <div className="flex items-center justify-end gap-3">
            <PopoverClose asChild>
              <Button type="button" variant="ghost" size="sm">
                Cancel
              </Button>
            </PopoverClose>
            <PopoverClose asChild>
              <Button type="button" variant="destructive" size="sm" onClick={onConfirm}>
                Delete
              </Button>
            </PopoverClose>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
